import type { PlanKey, WorkflowKind, WorkflowTrigger } from "@sagot/constants";
import type { Definition } from "./types";

/**
 * The template library (WF-P4-08) is `contracts/workflow-templates.json`: the backend validates and publishes every
 * definition in it, and this module reads the same file, so both sides share one source. A template points at the
 * workspace's own rows (a booking action, an endpoint, a team ...) with a sentinel id; `fillTemplate` swaps them.
 */
export const PLACEHOLDER_NAMES = ["booking_action", "endpoint", "team", "user", "lead_stage"] as const;
export type PlaceholderName = (typeof PLACEHOLDER_NAMES)[number];

/** Text in the languages the library is written in (`en` is the primary language of every definition). */
export interface LocalizedText {
  en: string;
  fil: string;
}

export interface TemplateEntry {
  id: string;
  trigger: WorkflowTrigger;
  kind: WorkflowKind;
  min_plan: PlanKey;
  /** The rows of the workspace the definition points at. */
  requires: PlaceholderName[];
  name: LocalizedText;
  description: LocalizedText;
  /** What the owner still does after creating it (a Home option, office hours, identity verification ...): the gallery says it before they commit. */
  setup: LocalizedText;
  definition: Definition;
}

export interface TemplateLibrary {
  version: number;
  placeholders: Record<PlaceholderName, string>;
  templates: TemplateEntry[];
}

/** The chosen row for each placeholder a template requires. */
export type TemplateChoices = Partial<Record<PlaceholderName, string>>;

/**
 * Loads the shared file on demand (it is code-split: only the template gallery needs it). The file is validated by the
 * backend's contract tests (`tests/services/workflows/test_templates_contract.py`), which is why a typed cast is enough here.
 */
export async function loadTemplateLibrary(): Promise<TemplateLibrary> {
  const file = await import("../../../../../../../contracts/workflow-templates.json");
  return file.default as unknown as TemplateLibrary;
}

/** The text in the console's language; Filipino for `fil`, English for anything else. */
export function localized(text: LocalizedText, language: string): string {
  return language === "fil" || language.startsWith("fil-") ? text.fil : text.en;
}

/** The template's definition with the workspace's rows in place of the sentinels. */
export function fillTemplate(entry: TemplateEntry, placeholders: Readonly<Record<PlaceholderName, string>>, choices: TemplateChoices): Definition {
  let text = JSON.stringify(entry.definition);
  for (const name of entry.requires) {
    const chosen = choices[name];
    if (chosen) text = text.replaceAll(placeholders[name], chosen);
  }
  return JSON.parse(text) as Definition;
}

/** The placeholders of a template for which the workspace has no row to choose from yet. */
export function missingRequirements(entry: TemplateEntry, options: Readonly<Record<PlaceholderName, readonly unknown[]>>): PlaceholderName[] {
  return entry.requires.filter((name) => options[name].length === 0);
}
